import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatRand, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, Clock, ShoppingBag } from "lucide-react";

const statusColor = {
  Processing: "bg-amber-100 text-amber-700",
  Packed: "bg-sky-100 text-sky-700",
  Shipped: "bg-purple-100 text-purple-700",
  Delivered: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-red-100 text-red-700",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/my").then((r) => {
      setOrders(r.data);
      setLoading(false);
    });
  }, []);

  return (
    <div data-testid="account-page" className="mx-auto max-w-5xl px-6 py-10">
      <div className="brand-gradient rounded-3xl p-8 text-white mb-8">
        <p className="text-white/80 text-sm">Welcome back,</p>
        <h1 className="font-display text-3xl font-bold">{user?.name}</h1>
        <p className="text-white/80 text-sm mt-1">{user?.email}</p>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold text-purple-950 flex items-center gap-2">
          <Package className="w-6 h-6 text-fuchsia-500" /> Order History
        </h2>
        <Link to="/shop" className="text-fuchsia-600 font-semibold hover:underline text-sm">Shop more →</Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => <div key={i} className="h-32 rounded-2xl bg-purple-50 animate-pulse" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-purple-100 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-7 h-7 text-purple-300" />
          </div>
          <p className="text-muted-foreground">You haven't placed any orders yet.</p>
          <Link to="/shop" className="inline-block mt-4 px-6 py-2.5 rounded-full brand-gradient text-white font-semibold">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.order_number}`} className="bg-white rounded-2xl border border-purple-100 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-purple-50">
                <div>
                  <p className="font-bold text-purple-950">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" /> {new Date(o.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor[o.status] || "bg-gray-100 text-gray-700"}`}>
                  {o.status}
                </span>
              </div>
              <div className="py-4 space-y-3">
                {o.items.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <img src={mediaUrl(it.image)} alt={it.name} className="w-12 h-14 rounded-lg object-contain bg-purple-50 p-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium line-clamp-1">{it.name}</p>
                      <p className="text-xs text-muted-foreground">{it.colour && `${it.colour} · `}Size {it.size} · Qty {it.quantity}</p>
                    </div>
                    <span className="text-sm font-semibold">{formatRand(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-purple-50">
                <span className="text-sm text-muted-foreground">{o.payment_status}</span>
                <span className="font-bold brand-gradient-text">{formatRand(o.total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
